// One wallet: what it holds now (GET /wallets/:chain/:address, read from the chain), how it traded
// over a window (…/analysis), every token it traded (…/positions) and its trades as they are indexed.
// Each part loads on its own, so a slow chain read holds up only the holdings.
import { ColorType, createChart, HistogramSeries, type IChartApi, type ISeriesApi, type UTCTimestamp } from "lightweight-charts";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { CHAINS, explorerAccount, explorerTx, tokenHref, useGet, useNow, walletHref, type ChainId } from "./api";
import { localTicks } from "./Chart";
import { age, amount, compact, price, share, short, usd } from "./format";
import { HeadStat, Split, tradeKey, useTrades } from "./Token";
import { A, ChainMark, Copy, Icon, Labels, Out, TokenLogo } from "./ui";

type TokenRef = { address: string; symbol: string | null; name: string | null };

type Position = {
  token: TokenRef;
  trades: number;
  buys: number;
  sells: number;
  bought: string;
  sold: string;
  open: string;
  boughtUsd: number;
  soldUsd: number;
  realizedPnlUsd: number | null;
  unrealizedPnlUsd: number | null;
  totalPnlUsd: number | null;
  lastTradeAt: number;
};

type Holding =
  | { kind: "token"; token: TokenRef; balance: string; priceUsd: number | null; valueUsd: number | null; allocation: number | null; thin: boolean; position: Omit<Position, "token"> | null }
  | { kind: "lp"; protocol: string; pool: string; tokens: { token: TokenRef }[]; valueUsd: number | null; allocation: number | null; thin: boolean };

type Portfolio = {
  labels: string[];
  indexedSince: number;
  totalValueUsd: number;
  pnl: { realizedUsd: number; unrealizedUsd: number; totalUsd: number };
  holdings: Holding[];
  count: number;
  fundedBy: { address: string; amount: string; time: number; labels: string[] } | null;
  fundedByReason: string | null;
};

type Bucket = { t: number; realizedPnlUsd: number };

type Analysis = {
  labels: string[];
  indexedSince: number;
  realizedPnlUsd: number;
  unrealizedPnlUsd: number;
  totalPnlUsd: number;
  boughtUsd: number;
  soldUsd: number;
  trades: number;
  buys: number;
  sells: number;
  tokens: number;
  wins: number;
  losses: number;
  winRate: number | null;
  distribution: Record<"gt500" | "200to500" | "50to200" | "0to50" | "minus50to0" | "ltMinus50", number>;
  timeline: Bucket[];
  best: Position[];
  worst: Position[];
};

const WINDOWS = ["1d", "7d", "30d", "90d"] as const;
type Win = (typeof WINDOWS)[number];
const WINDOW_NAMES: Record<Win, string> = { "1d": "Last day", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days" };

// Logos are open and served by the API; a token without one answers 404 and shows its letter.
const logo = (chain: ChainId, address: string) => (address === "native" ? null : `https://api.aurelia.build/logos/${chain}/${address}`);

const signedUsd = (v: number | null | undefined) => (v == null ? "–" : `${v > 0 ? "+" : ""}${usd(v)}`);
const tone = (v: number | null | undefined) => (v == null || v === 0 ? undefined : v > 0 ? ("up" as const) : ("down" as const));

function Pnl({ value }: { value: number | null | undefined }) {
  return <span className={`num ${tone(value) ?? ""}`}>{signedUsd(value)}</span>;
}

export function Wallet({ chain, address }: { chain: ChainId; address: string }) {
  const [win, setWin] = useState<Win>("7d");
  const portfolio = useGet<Portfolio>(`/wallets/${chain}/${address}?limit=100`, 60_000);
  const analysis = useGet<Analysis>(`/wallets/${chain}/${address}/analysis?window=${win}`, 60_000);
  const p = portfolio.data;
  const a = analysis.data;

  useEffect(() => void (document.title = `Wallet ${short(address)} – Aurelia Screener`), [address]);

  if (portfolio.error && analysis.error) {
    return (
      <div className="empty-page">
        <h1>This wallet could not be loaded</h1>
        <p className="muted">{portfolio.error.message}</p>
      </div>
    );
  }

  return (
    <div className="page token-page">
      <header className="card token-head">
        <div className="token-id">
          <span className="wallet-avatar">
            <Icon name="wallet" size={22} />
            <span className="token-logo-chain">
              <ChainMark chain={chain} size={14} />
            </span>
          </span>
          <div className="token-title">
            <h1>{short(address)}</h1>
            <span className="token-sub">
              <span className="muted">{CHAINS[chain].name} wallet</span>
              <span className="addr">
                <Copy text={address} label="Copy the wallet address" />
                <Out href={explorerAccount(chain, address)} className="icon-link" title={`Open in ${CHAINS[chain].explorerName}`}>
                  <Icon name="out" size={15} />
                </Out>
              </span>
              <Labels labels={p?.labels ?? a?.labels ?? []} />
            </span>
          </div>
        </div>
        <div className="head-stats">
          <HeadStat label="Value" value={usd(p?.totalValueUsd)} />
          <HeadStat label="Total PnL" value={signedUsd(p?.pnl.totalUsd)} tone={tone(p?.pnl.totalUsd)} />
          <HeadStat label="Realized" value={signedUsd(p?.pnl.realizedUsd)} tone={tone(p?.pnl.realizedUsd)} />
          <HeadStat label="Unrealized" value={signedUsd(p?.pnl.unrealizedUsd)} tone={tone(p?.pnl.unrealizedUsd)} />
          <HeadStat label="Holdings" value={p ? compact(p.count) : "–"} />
        </div>
      </header>

      <div className="token-grid">
        <div className="token-main">
          <section className="card chart-card">
            <div className="chart-toolbar">
              <div className="segmented small" role="group" aria-label="Period">
                {WINDOWS.map((w) => (
                  <button key={w} className={w === win ? "seg active" : "seg"} onClick={() => setWin(w)}>
                    {w}
                  </button>
                ))}
              </div>
              <span className="muted chart-note">Realized profit per {win === "1d" ? "hour" : "day"}</span>
            </div>
            <PnlChart timeline={a?.timeline ?? null} error={analysis.error?.message} />
          </section>
          <WalletTabs chain={chain} address={address} portfolio={portfolio} />
        </div>
        <aside className="token-side">
          <Performance a={a} win={win} error={analysis.error?.message} />
          {a && <Distribution d={a.distribution} />}
          {a && (a.best.length > 0 || a.worst.length > 0) && <BestWorst a={a} chain={chain} />}
          <About p={p} chain={chain} />
        </aside>
      </div>
    </div>
  );
}

// ─── The profit chart: one bar per bucket, up or down from zero ───

function PnlChart({ timeline, error }: { timeline: Bucket[] | null; error?: string }) {
  const el = useRef<HTMLDivElement>(null);
  const api = useRef<{ chart: IChartApi; bars: ISeriesApi<"Histogram"> } | null>(null);

  useEffect(() => {
    const chart = createChart(el.current!, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#94a3b8", fontFamily: "'Instrument Sans Variable', system-ui, sans-serif", fontSize: 12 },
      grid: { vertLines: { visible: false }, horzLines: { color: "#0d0f13" } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, tickMarkFormatter: localTicks, maxBarSpacing: 22 },
      localization: {
        priceFormatter: usd,
        timeFormatter: (t: number) => new Date(t * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }),
      },
      handleScroll: false,
      handleScale: false,
    });
    const bars = chart.addSeries(HistogramSeries, { priceFormat: { type: "custom", formatter: usd, minMove: 0.01 }, priceLineVisible: false, lastValueVisible: false });
    api.current = { chart, bars };
    return () => {
      chart.remove();
      api.current = null;
    };
  }, []);

  useEffect(() => {
    if (!api.current || !timeline) return;
    api.current.bars.setData(timeline.map((b) => ({ time: (b.t / 1000) as UTCTimestamp, value: b.realizedPnlUsd, color: b.realizedPnlUsd >= 0 ? "#00d26a" : "#ff3b4e" })));
    api.current.chart.timeScale().fitContent();
  }, [timeline]);

  const note = error ? `The profit chart could not be loaded (${error}).` : !timeline ? "Loading…" : !timeline.length ? "No sells in this period." : null;
  return (
    <div className="wallet-pnl">
      <div className="chart-host" ref={el} />
      {note && <p className="chart-status muted">{note}</p>}
    </div>
  );
}

// ─── The side ───

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="wallet-row">
      <span className="muted">{label}</span>
      {children}
    </div>
  );
}

function Performance({ a, win, error }: { a: Analysis | null; win: Win; error?: string }) {
  return (
    <section className="card side-card">
      <div className="split-head">
        <span className="stat-label">{WINDOW_NAMES[win]}</span>
        <Pnl value={a?.realizedPnlUsd} />
      </div>
      {!a ? (
        <p className="wallet-note">{error ? `Could not be loaded (${error}).` : "Loading…"}</p>
      ) : (
        <>
          <Split label="Win rate" total={a.winRate == null ? "–" : share(a.winRate)} a={a.wins} b={a.losses} aLabel={`${compact(a.wins)} won`} bLabel={`${compact(a.losses)} lost`} />
          <Split label="Txns" total={compact(a.trades)} a={a.buys} b={a.sells} aLabel={`${compact(a.buys)} buys`} bLabel={`${compact(a.sells)} sells`} />
          <Split label="Volume" total={usd(a.boughtUsd + a.soldUsd)} a={a.boughtUsd} b={a.soldUsd} aLabel={`${usd(a.boughtUsd)} bought`} bLabel={`${usd(a.soldUsd)} sold`} />
          <div className="wallet-rows">
            <Row label="Tokens traded">
              <span className="num">{compact(a.tokens)}</span>
            </Row>
          </div>
        </>
      )}
    </section>
  );
}

const BUCKETS: { key: keyof Analysis["distribution"]; label: string; up: boolean }[] = [
  { key: "gt500", label: "Over 500%", up: true },
  { key: "200to500", label: "200–500%", up: true },
  { key: "50to200", label: "50–200%", up: true },
  { key: "0to50", label: "0–50%", up: true },
  { key: "minus50to0", label: "0 to -50%", up: false },
  { key: "ltMinus50", label: "Under -50%", up: false },
];

function Distribution({ d }: { d: Analysis["distribution"] }) {
  const max = Math.max(1, ...BUCKETS.map((b) => d[b.key]));
  return (
    <section className="card side-card">
      <span className="stat-label">Tokens sold, by return</span>
      <div className="wallet-dist">
        {BUCKETS.map((b) => (
          <div key={b.key} style={{ display: "contents" }}>
            <span className="muted">{b.label}</span>
            <span>{d[b.key] > 0 && <span className="wallet-dist-bar" style={{ width: `${(d[b.key] / max) * 100}%`, background: b.up ? "var(--up)" : "var(--down)" }} />}</span>
            <span className="num right-text">{d[b.key]}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BestWorst({ a, chain }: { a: Analysis; chain: ChainId }) {
  const list = (name: string, rows: Position[]) =>
    rows.length > 0 && (
      <>
        <span className="stat-label">{name}</span>
        <div className="wallet-rows">
          {rows.map((p) => (
            <div key={p.token.address} className="wallet-row">
              <TokenLink chain={chain} token={p.token} />
              <Pnl value={p.realizedPnlUsd} />
            </div>
          ))}
        </div>
      </>
    );
  return (
    <section className="card side-card">
      {list("Best trades", a.best)}
      {list("Worst trades", a.worst)}
    </section>
  );
}

function About({ p, chain }: { p: Portfolio | null; chain: ChainId }) {
  if (!p) return null;
  return (
    <section className="card side-card">
      <span className="stat-label">First funding</span>
      {p.fundedBy ? (
        <p className="wallet-note">
          {amount(p.fundedBy.amount)} from <A href={walletHref(chain, p.fundedBy.address)}>{short(p.fundedBy.address)}</A>, {age(p.fundedBy.time)} ago
        </p>
      ) : (
        <p className="wallet-note">{p.fundedByReason ? p.fundedByReason[0]!.toUpperCase() + p.fundedByReason.slice(1) + "." : "Not known."}</p>
      )}
      <p className="wallet-note">PnL covers trades since indexing began {age(p.indexedSince)} ago.</p>
    </section>
  );
}

// ─── The lists ───

function TokenLink({ chain, token }: { chain: ChainId; token: TokenRef }) {
  const symbol = token.symbol ?? short(token.address);
  // Native SOL, BNB or ETH has no token page.
  return token.address === "native" ? <span>{symbol}</span> : <A href={tokenHref(chain, token.address)}>{symbol}</A>;
}

function TokenCell({ chain, token, badge }: { chain: ChainId; token: TokenRef; badge?: ReactNode }) {
  return (
    <span className="td token-col">
      <TokenLogo src={logo(chain, token.address)} symbol={token.symbol} size={26} />
      <span className="token-names">
        <strong className="ellipsis">{token.symbol ?? short(token.address)}</strong>
        <span className="muted ellipsis">{token.name}</span>
      </span>
      {badge}
    </span>
  );
}

const TABS = ["Holdings", "Positions", "Trades"] as const;

function WalletTabs({ chain, address, portfolio }: { chain: ChainId; address: string; portfolio: { data: Portfolio | null; error: { message: string } | null } }) {
  const [tab, setTab] = useState<(typeof TABS)[number]>("Holdings");
  return (
    <section className="card tabs-card">
      <div className="tabs" role="tablist">
        {TABS.map((x) => (
          <button key={x} role="tab" aria-selected={tab === x} className={tab === x ? "tab active" : "tab"} onClick={() => setTab(x)}>
            {x}
          </button>
        ))}
      </div>
      {tab === "Holdings" && <Holdings chain={chain} portfolio={portfolio} />}
      {tab === "Positions" && <Positions chain={chain} address={address} />}
      {tab === "Trades" && <WalletTrades chain={chain} address={address} />}
    </section>
  );
}

function Holdings({ chain, portfolio }: { chain: ChainId; portfolio: { data: Portfolio | null; error: { message: string } | null } }) {
  const { data, error } = portfolio;
  if (!data) return <p className="table-note muted">{error ? `Holdings could not be read right now (${error.message}).` : "Reading balances from the chain…"}</p>;
  if (!data.holdings.length) return <p className="table-note muted">This wallet holds nothing Aurelia can see.</p>;
  return (
    <div className="table holdings-table" role="table">
      <div className="tr thead" role="row">
        <span className="th token-col">Token</span>
        <span className="th hide-sm">Balance</span>
        <span className="th hide-sm">Price</span>
        <span className="th">Value</span>
        <span className="th hide-sm">Share</span>
        <span className="th">PnL</span>
      </div>
      {data.holdings.map((h, i) => {
        // A thin token traded under $1,000 in 24 h: the API shows it but does not count it.
        const thin = h.thin ? (
          <span className="badge" title="Traded under $1,000 in 24 hours: shown, not counted in the value">
            thin
          </span>
        ) : undefined;
        if (h.kind === "lp") {
          const name = h.tokens.map((t) => t.token.symbol ?? "?").join(" / ");
          return (
            <div key={`lp:${h.pool}:${i}`} className="tr row" role="row">
              <span className="td token-col">
                <span className="token-names">
                  <strong className="ellipsis">{name}</strong>
                  <span className="muted ellipsis">{h.protocol.replace(/-/g, " ")} liquidity</span>
                </span>
                {thin}
              </span>
              <span className="td hide-sm" />
              <span className="td hide-sm" />
              <span className="td num">{usd(h.valueUsd)}</span>
              <span className="td num hide-sm">{share(h.allocation)}</span>
              <span className="td" />
            </div>
          );
        }
        const cells = (
          <>
            <TokenCell chain={chain} token={h.token} badge={thin} />
            <span className="td num hide-sm">{amount(h.balance)}</span>
            <span className="td num hide-sm">{price(h.priceUsd)}</span>
            <span className="td num">{usd(h.valueUsd)}</span>
            <span className="td num hide-sm">{share(h.allocation)}</span>
            <span className="td">
              <Pnl value={h.position?.totalPnlUsd} />
            </span>
          </>
        );
        return h.token.address === "native" ? (
          <div key="native" className="tr row" role="row">
            {cells}
          </div>
        ) : (
          <A key={h.token.address} href={tokenHref(chain, h.token.address)} className="tr row">
            {cells}
          </A>
        );
      })}
    </div>
  );
}

const SORTS = [
  ["pnl", "PnL"],
  ["recent", "Recent"],
  ["volume", "Volume"],
] as const;

function Positions({ chain, address }: { chain: ChainId; address: string }) {
  const [sort, setSort] = useState<(typeof SORTS)[number][0]>("pnl");
  const { data, error } = useGet<{ positions: Position[] }>(`/wallets/${chain}/${address}/positions?sort=${sort}&limit=100`, 60_000);
  const now = useNow(30_000);
  return (
    <>
      <div className="segmented small wallet-sort" role="group" aria-label="Sort by">
        {SORTS.map(([k, name]) => (
          <button key={k} className={k === sort ? "seg active" : "seg"} onClick={() => setSort(k)}>
            {name}
          </button>
        ))}
      </div>
      {error && !data && <p className="table-note muted">Positions could not be loaded ({error.message}).</p>}
      {!data && !error && <p className="table-note muted">Loading positions…</p>}
      {data && !data.positions.length && <p className="table-note muted">No trade since indexing began.</p>}
      {data && data.positions.length > 0 && (
        <div className="table positions-table" role="table">
          <div className="tr thead" role="row">
            <span className="th token-col">Token</span>
            <span className="th hide-sm">Bought</span>
            <span className="th hide-sm">Sold</span>
            <span className="th hide-md">Realized</span>
            <span className="th hide-md">Unrealized</span>
            <span className="th">PnL</span>
            <span className="th hide-sm">Txns</span>
            <span className="th">Last</span>
          </div>
          {data.positions.map((p) => {
            const cells = (
              <>
                <TokenCell chain={chain} token={p.token} />
                <span className="td num hide-sm">{usd(p.boughtUsd)}</span>
                <span className="td num hide-sm">{usd(p.soldUsd)}</span>
                <span className="td hide-md">
                  <Pnl value={p.realizedPnlUsd} />
                </span>
                <span className="td hide-md">
                  <Pnl value={p.unrealizedPnlUsd} />
                </span>
                <span className="td">
                  <Pnl value={p.totalPnlUsd} />
                </span>
                <span className="td num muted hide-sm">
                  {p.buys}/{p.sells}
                </span>
                <span className="td num muted">{age(p.lastTradeAt, now)}</span>
              </>
            );
            return p.token.address === "native" ? (
              <div key="native" className="tr row" role="row">
                {cells}
              </div>
            ) : (
              <A key={p.token.address} href={tokenHref(chain, p.token.address)} className="tr row">
                {cells}
              </A>
            );
          })}
        </div>
      )}
    </>
  );
}

function WalletTrades({ chain, address }: { chain: ChainId; address: string }) {
  const trades = useTrades(chain, "wallet", address);
  const now = useNow(1000);
  if (!trades) return <p className="table-note muted">Loading trades…</p>;
  if (!trades.length) return <p className="table-note muted">No trade since indexing began.</p>;
  return (
    <div className="table wallet-trades-table" role="table">
      <div className="tr thead" role="row">
        <span className="th left-text">Age</span>
        <span className="th left-text">Type</span>
        <span className="th left-text">Token</span>
        <span className="th">USD</span>
        <span className="th hide-sm">Amount</span>
        <span className="th hide-sm">Price</span>
        <span className="th tx-col" />
      </div>
      {trades.map((t) => (
        <div key={tradeKey(t)} className="tr row" role="row">
          <span className="td muted num left-text">{age(t.time, now)}</span>
          <span className={`td left-text side-${t.side}`}>{t.side === "buy" ? "Buy" : "Sell"}</span>
          <span className="td left-text ellipsis">
            <TokenLink chain={chain} token={{ ...t.token, name: null }} />
          </span>
          <span className={`td num side-${t.side}`}>{usd(t.amountUsd)}</span>
          <span className="td num hide-sm">{amount(t.amount)}</span>
          <span className="td num hide-sm">{price(t.priceUsd)}</span>
          <span className="td tx-col">
            <Out href={explorerTx(chain, t.tx)} className="icon-link" title="Open the transaction">
              <Icon name="out" size={14} />
            </Out>
          </span>
        </div>
      ))}
    </div>
  );
}
